import { Box, Typography, TextField, Button } from "@mui/material";
import { ImagePlus, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Star, StarBorder } from "@mui/icons-material";
import { api } from "../../api/api";
import { useForm } from "react-hook-form";
import { orderhistory_api } from "../../api/urls";
import { toast } from "react-toastify";

const Review = ({ product, onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [rating, setRating] = useState(0);

  const { register, handleSubmit, setValue } = useForm({
    defaultValues: {
      rate_1: 0,
      rate_2: 0,
      rate_3: 0,
      rate_4: 0,
      rate_5: 0,
      review_text: "",

      product_review: product?.product,
    },
  });

  useEffect(() => {
    if (product?.product) {
      setValue("product_review", product?.product);
    }
  }, [product, setValue]);
  // console.log(product,"i am the product")
  useEffect(() => {
    for (let i = 1; i <= 5; i++) {
      setValue(`rate_${i}`, i === rating ? 1 : 0, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [rating, setValue]);


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Filter only allowed types
    const validFiles = files.filter(
      (f) =>
        f.type.startsWith("video/") ||
        f.type === "image/jpeg" ||
        f.type === "image/png",
    );
    if (validFiles.length === 0) return;

    // Limit total files to 5
    const totalFiles = selectedFiles.length + validFiles.length;
    if (totalFiles > 5) {
      toast.error("You can upload up to 5 files only!");
      validFiles.splice(5 - selectedFiles.length); // keep only remaining slots
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    const newPreviews = validFiles.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  useEffect(() => {
    return () => previews.forEach((url) => URL.revokeObjectURL(url));
  }, [previews]);



  const objToFormdata = (data, files) => {
    const formData = new FormData();

    // Append normal fields
    for (const key in data) {
      if (data[key] != null) formData.append(key, data[key]);
    }

    // Append files dynamically, fill empty strings if not uploaded
    for (let i = 0; i < 5; i++) {
      if (files[i]) {
        formData.append(`upload0${i + 1}`, files[i]); // file exists
      } else {
        formData.append(`upload0${i + 1}`, ""); // empty string
      }
    }

    return formData;
  };


  const onSubmit = async (data) => {
    try {
      const orderId = product?.id;

      // Convert data + selected files to FormData
      const formData = objToFormdata(data, selectedFiles);

      await api.patch(`${orderhistory_api}${orderId}/write_review/`, formData);

      // console.log("Review submitted:", response.data);
      toast.success("Review submitted successfully!");
      onClose();
    } catch (err) {
      console.error("Error submitting review:", err);
      if (err.response) {
        const backendMessage =
          err.response.data?.data || "Something went wrong!";
        toast.error(backendMessage);
      } else {
        toast.error("Something went wrong!");
      }
    }
  };

  const showIcon = selectedFiles.length === 0;

  return (
    <Box
      sx={{
        maxWidth: 800,
        width: "100%",
        mx: "auto",
        p: 2,
        bgcolor: "#fff",
        borderRadius: 2,
      }}
    >
      {/* Product info */}
      <div className={"flex-wrap flex gap-4"}>
        <div className="w-[180px] h-[180px] rounded-lg">
          <img
            src={product?.image}
            alt="Product"
            className="object-cover rounded-lg w-[180px] h-[180px]"
          />
        </div>
        <div>
          <Typography variant="h6" fontWeight={600} mb={3}>
            {product?.title}
          </Typography>
          <Typography variant="body2" mb={3}>
            {product?.color && <strong>Color:</strong>}
            {product?.color}{" "}
            <strong style={{ marginLeft: "12px" }}>Quantity:</strong>{" "}
            {product?.quantity}
          </Typography>

          <div className="flex gap-2 flex-wrap">
            <div>
              <strong>Order Date:</strong>{" "}
              {product?.orderDate
                ? new Date(product.orderDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  })
                : ""}
            </div>{" "}
            <div>
              <strong>Order ID:</strong> {product?.orderId}
            </div>
          </div>

          <div className="flex gap-2 mt-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <div
                key={star}
                onClick={() => setRating(star)}
                style={{ cursor: "pointer" }}
              >
                {star <= rating ? (
                  <Star style={{ color: "#f7d65a", fontSize: "40px" }} />
                ) : (
                  <StarBorder style={{ color: "#b9b9b9", fontSize: "40px" }} />
                )}
              </div>
            ))}

            {[1, 2, 3, 4, 5].map((i) => (
              <input key={i} type="hidden" {...register(`rate_${i}`)} />
            ))}
            <input type="hidden" {...register("product_review")} />
          </div>
        </div>
      </div>

      {/* Review Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={2} className={"mt-2"}>
          <Typography variant="h6" fontSize={17}>
            Write your review here
          </Typography>
          <TextField
            label="Write your review"
            {...register("review_text")}
            multiline
            rows={4}
            variant="outlined"
            fullWidth
          />

          <Typography variant="h6" fontSize={17}>
            Upload Image/Video
          </Typography>

          <Box
            sx={{
              height: "150px",
              border: "2px dashed #b9b9b9",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: previews.length === 0 ? "center" : "flex-start",
              justifyContent: previews.length === 0 ? "center" : "flex-start",
              cursor: "pointer",
              bgcolor: "#fafafa",
              position: "relative",
              p: 1,
            }}
          >
            {showIcon && <ImagePlus size={40} color="#b9b9b9" />}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {previews.map((url, index) => {
                const file = selectedFiles[index];
                if (!file) return null;

                return (

                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      width: 60,
                      height: 80,
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    {file.type.startsWith("video/") ? (
                      <video
                        src={url}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        controls
                      />
                    ) : (
                      <img
                        src={url}
                        alt={file.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}

                    {/* Top-right cross */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: -5,
                        right: -5,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: "0 0 2px rgba(0,0,0,0.5)",
                        zIndex: 10, // make sure it's above the file input
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // stops click from triggering input
                        setSelectedFiles((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                        setPreviews((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      <X size={12} />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <input
              type="file"
              accept=".jpg,.jpeg,.png,video/*"
              multiple
              onChange={handleFileChange}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
              }}
            />
          </Box>

          <Box display="flex" mt={2} gap="12px">
            <Button
              color="inherit"
              onClick={onClose}
              sx={{ border: "1px solid #b9b9b9" }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              sx={{ bgcolor: "black" }}
              className="flex-1"
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default Review;
